import React from "react";
import { Box, TextField } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

// Click-to-edit text cell. Click (or focus + Enter) to enter edit mode,
// commit on blur or Enter, bail on Escape. Shows ``displayValue`` (or
// the raw value) when read-only. Used by Listings and Inventory Detail
// rows.
//
// Affordance: a permanent dotted underline + a pencil icon that fades
// in on hover. The original "subtle action.hover bg only" version was
// invisible enough that operators assumed cells were static text and
// the labeled action buttons (Discount, Undercut, etc.) were the only
// way to edit a listing. The pencil + underline tell the eye "this
// cell is interactive" at a glance.
export default function EditableTextCell({
  value,
  displayValue,
  type = "text",
  align = "left",
  width,
  busy,
  disabled,
  onCommit,
  inputProps,
  placeholder,
}) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState("");

  const startEdit = () => {
    // Disabled blocks edits hard. ``busy`` (a save is in flight on
    // THIS specific cell) is treated as a soft hint only — we still
    // let the user re-open the editor. That lets the operator type a
    // correction immediately if they realize the value they just
    // committed was wrong, without waiting for the in-flight PATCH
    // to round-trip first.
    if (disabled) return;
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  };

  const commit = () => {
    setEditing(false);
    if (draft === (value == null ? "" : String(value))) return;
    onCommit?.(draft);
  };

  if (editing) {
    return (
      <TextField
        autoFocus
        size="small"
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
        }}
        placeholder={placeholder}
        inputProps={{ ...inputProps, style: { textAlign: align } }}
        sx={{ width: width || "100%" }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  const interactive = !disabled;
  return (
    <Box
      role="button"
      tabIndex={interactive ? 0 : -1}
      onClick={(e) => { e.stopPropagation(); startEdit(); }}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); startEdit(); } }}
      sx={{
        position: "relative",
        cursor: interactive ? "text" : "default",
        opacity: busy ? 0.5 : 1,
        textAlign: align,
        px: 0.5,
        py: 0.25,
        borderRadius: 1,
        // Permanent dotted underline (light gray) signals "interactive
        // text" without being noisy. Stronger underline + a faint bg
        // on hover so the cursor target is obvious. Dashed-underline +
        // pencil-on-hover is a long-standing "click to edit" pattern.
        ...(interactive && {
          borderBottom: "1px dotted",
          borderBottomColor: "divider",
        }),
        "&:hover": interactive ? {
          bgcolor: "action.hover",
          borderBottomColor: "primary.main",
          "& .edit-icon": { opacity: 0.7 },
        } : {},
        "&:focus-visible": interactive ? {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 1,
        } : {},
      }}
      title={interactive ? "Click to edit" : ""}
    >
      {displayValue ?? value ?? <Box component="span" sx={{ color: "text.disabled" }}>—</Box>}
      {interactive && (
        <EditOutlinedIcon
          className="edit-icon"
          sx={{
            position: "absolute",
            top: 2,
            right: align === "right" ? "unset" : 2,
            left: align === "right" ? 2 : "unset",
            fontSize: 11,
            opacity: 0,
            transition: "opacity 120ms",
            color: "primary.main",
            pointerEvents: "none",
          }}
        />
      )}
    </Box>
  );
}
